import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const USE_MOCK = process.env.REACT_APP_USE_MOCK !== 'false';

// Declare named exports (implementations will be assigned below)
let fetchCantiques;
let getCantique;
let createCantique;
let updateCantique;
let deleteCantique;
let uploadCantiqueAudio;
let fetchRenumberPreview;
let applyRenumber;

let fetchLangues;
let createLangue;
let updateLangue;
let deleteLangue;

let login;
let register;

let sendVisitorHeartbeat;
let fetchVisitors;
let deleteVisitor;
let deleteAllVisitors;

let apiDefault = null;

if (!USE_MOCK) {
  const instance = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // attach token if present
  instance.interceptors.request.use(config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  });

  // handle 401 errors
  instance.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401) {
        // token invalid or expired, clear it and show modal
        localStorage.removeItem('token');
        window.dispatchEvent(new CustomEvent('authError'));
      }
      return Promise.reject(error);
    }
  );

  // assign real implementations
  fetchCantiques = () => instance.get('/cantiques');
  getCantique = (id) => instance.get(`/cantiques/${id}`);
  createCantique = (data) => instance.post('/cantiques', data);
  updateCantique = (id, data) => instance.put(`/cantiques/${id}`, data);
  deleteCantique = (id) => instance.delete(`/cantiques/${id}`);
  fetchRenumberPreview = () => instance.get('/cantiques/renumber');
  applyRenumber = () => instance.post('/cantiques/renumber');
  uploadCantiqueAudio = (file, filename) => {
    const formData = new FormData();
    // if filename provided, use it as the sent filename and include as field
    if (filename) {
      formData.append('audio', file, filename);
      formData.append('filename', filename);
    } else {
      formData.append('audio', file, file.name || 'audio.webm');
    }
    return instance.post('/cantiques/audio', formData);
  };

  fetchLangues = () => instance.get('/langues');
  createLangue = (data) => instance.post('/langues', data);
  updateLangue = (id, data) => instance.put(`/langues/${id}`, data);
  deleteLangue = (id) => instance.delete(`/langues/${id}`);

  login = (credentials) => instance.post('/auth/login', credentials);
  register = (credentials) => instance.post('/auth/register', credentials);

  sendVisitorHeartbeat = (data) => instance.post('/visitors/heartbeat', data);
  fetchVisitors = () => instance.get('/visitors');
  deleteVisitor = (id) => instance.delete(`/visitors/${id}`);
  deleteAllVisitors = () => instance.delete('/visitors');

  apiDefault = instance;
} else {
  // --- Mock implementation for frontend-only development ---
  const delay = (ms) => new Promise(res => setTimeout(res, ms));

  // sample mock data
  let mockLangues = [
    { _id: 'lang1', name: 'Français' },
    { _id: 'lang2', name: 'English' }
  ];

  let mockCantiques = [
    {
      _id: 'c1',
      number: 1,
      title: 'Débout sainte cohorte',
      langue: 'Français',
      refrain: 'Gloire, honneur à notre Roi',
      audio: true,
      audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3',
      couplets: [
        { text: 'Débout sainte cohorte, pour combattre sous ses lois' },
        { text: 'Que la bannière se transporte, vers les peuples et les rois' }
      ]
    },
    {
      _id: 'c2',
      number: 2,
      title: 'Je veux chanter la gloire',
      langue: 'Français',
      refrain: 'Alléluia, Alléluia',
      audio: true,
      audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3',
      couplets: [
        { text: 'Je veux chanter la gloire du Seigneur éternel' },
        { text: 'Sa bonté demeure à jamais' }
      ]
    },
    {
      _id: 'c3',
      number: 3,
      title: 'Christ est ressuscité',
      langue: 'Français',
      refrain: 'Victoire, victoire au Seigneur',
      audio: true,
      audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3',
      couplets: [
        { text: 'Christ est ressuscité des morts, Alléluia' },
        { text: 'Il a vaincu le trépas par sa puissance' }
      ]
    },
    {
      _id: 'c4',
      number: 13,
      title: 'Quelle brillante aurore',
      langue: 'Français',
      refrain: 'Hosanna, béni soit le Roi',
      couplets: [
        { text: 'Quelle brillante aurore se lève sur nos fronts' },
        { text: 'Quel beau jour daigne enfin nous venir' }
      ]
    },
    {
      _id: 'c5',
      number: 25,
      title: 'Mon âme, chante les louanges',
      langue: 'Français',
      refrain: 'Gloire et honneur au Tout-Puissant',
      couplets: [
        { text: 'Mon âme, chante les louanges du Seigneur' },
        { text: 'Que mon coeur exulte en sa présence' }
      ]
    },
    {
      _id: 'c6',
      number: 42,
      title: 'High above the starry sky',
      langue: 'English',
      refrain: 'Forever, forever, O Lord',
      couplets: [
        { text: 'High above the starry sky, Thy glory shines' },
        { text: 'Through all eternity, Thy love divine' }
      ]
    },
    {
      _id: 'c7',
      number: 47,
      title: 'Lumière du matin',
      langue: 'Français',
      refrain: 'Ta lumière éclaire nos vies',
      couplets: [
        { text: 'Lumière du matin, couvrez nos chemins' },
        { text: 'Guide nos pas, Seigneur, vers ton amour' }
      ]
    },
    {
      _id: 'c8',
      number: 58,
      title: 'Soleil de justice',
      langue: 'Français',
      refrain: 'Il vient nous sauver',
      couplets: [
        { text: 'Soleil de justice, lève-toi sur nous' },
        { text: 'Réchauffe nos cœurs, allume notre foi' }
      ]
    },
    {
      _id: 'c9',
      number: 63,
      title: 'Venez, adorons',
      langue: 'Français',
      refrain: 'Adorons le Seigneur',
      couplets: [
        { text: 'Venez, adorons, chantons sa grandeur' },
        { text: 'Louons son nom, sa bonté, sa faveur' }
      ]
    },
    {
      _id: 'c10',
      number: 77,
      title: 'Sous ton aile',
      langue: 'Français',
      refrain: 'Je suis sûr sous ton aile',
      couplets: [
        { text: 'Sous ton aile, je trouve la paix' },
        { text: 'Ton amour me protège et me garde à jamais' }
      ]
    },
    {
      _id: 'c11',
      number: 82,
      title: 'Every heart will sing',
      langue: 'English',
      refrain: 'Every heart will sing your praise',
      couplets: [
        { text: 'In every season, I will lift my voice' },
        { text: 'Your mercy leads me, and I rejoice' }
      ]
    },
    {
      _id: 'c12',
      number: 90,
      title: 'Hosanna dans les cieux',
      langue: 'Français',
      refrain: 'Hosanna, louange éternelle',
      couplets: [
        { text: 'Hosanna dans les cieux, chantons ta gloire' },
        { text: 'Ton règne vient, ô Roi, pour l’éternité' }
      ]
    },
    {
      _id: 'c13',
      number: 96,
      title: 'Joyful noise',
      langue: 'English',
      refrain: 'Make a joyful noise',
      couplets: [
        { text: 'Lift your voice and sing with praise' },
        { text: 'For the Lord is worthy of all our days' }
      ]
    },
    {
      _id: 'c14',
      number: 102,
      title: 'Merveille des âges',
      langue: 'Français',
      refrain: 'Merveille des âges, mon espérance',
      couplets: [
        { text: 'Tu es la force qui me tient debout' },
        { text: 'L’espoir en toi éclaire chaque route' }
      ]
    },
    {
      _id: 'c15',
      number: 108,
      title: 'Shine the light',
      langue: 'English',
      refrain: 'Shine your light upon us',
      couplets: [
        { text: 'In every shadow, we will stand' },
        { text: 'Your love surrounds us like a hand' }
      ]
    },
    {
      _id: 'c16',
      number: 115,
      title: 'Paix de mon âme',
      langue: 'Français',
      refrain: 'Ta paix règne en mon âme',
      couplets: [
        { text: 'Quand les vagues montent, tu es mon port' },
        { text: 'Je demeure tranquille en ton réconfort' }
      ]
    },
    {
      _id: 'c17',
      number: 121,
      title: 'Heart of worship',
      langue: 'English',
      refrain: 'I bring my heart of worship',
      couplets: [
        { text: 'Let every breath declare your praise' },
        { text: 'I come with humble heart to you always' }
      ]
    },
    {
      _id: 'c18',
      number: 128,
      title: 'Dans tes mains',
      langue: 'Français',
      refrain: 'Je remets ma vie entre tes mains',
      couplets: [
        { text: 'Dans tes mains, je trouve ma force' },
        { text: 'Sous ta garde, je vis sans crainte' }
      ]
    },
    {
      _id: 'c19',
      number: 135,
      title: 'Grace upon us',
      langue: 'English',
      refrain: 'Grace upon us, mercy poured down',
      couplets: [
        { text: 'We stand forgiven by your grace' },
        { text: 'Your love surrounds us in every place' }
      ]
    },
    {
      _id: 'c20',
      number: 142,
      title: 'Feu du Ciel',
      langue: 'Français',
      refrain: 'Feu du ciel, embrase nos cœurs',
      couplets: [
        { text: 'Viens remplir nos vies de ta flamme' },
        { text: 'Que ton Esprit brûle et nous enflamme' }
      ]
    },
    {
      _id: 'c21',
      number: 149,
      title: 'Blessed be the name',
      langue: 'English',
      refrain: 'Blessed be the name of the Lord',
      couplets: [
        { text: 'We lift his name in every song' },
        { text: 'To him belongs our praise all night long' }
      ]
    },
    {
      _id: 'c22',
      number: 155,
      title: 'Louange sans fin',
      langue: 'Français',
      refrain: 'Louange sans fin pour notre Roi',
      couplets: [
        { text: 'Ton amour nous guide, ta voix nous porte' },
        { text: 'Nous célébrons ta gloire sans détour' }
      ]
    },
    {
      _id: 'c23',
      number: 161,
      title: 'King of Glory',
      langue: 'English',
      refrain: 'King of Glory, we adore you',
      couplets: [
        { text: 'You reign forever, strong and true' },
        { text: 'Our lives belong to you alone' }
      ]
    },
    {
      _id: 'c24',
      number: 168,
      title: 'Cantique de l’aube',
      langue: 'Français',
      refrain: 'L’aube chante ta fidélité',
      couplets: [
        { text: 'Aube nouvelle, notre Dieu est là' },
        { text: 'Sa grâce brille sur nous chaque fois' }
      ]
    },
    {
      _id: 'c25',
      number: 174,
      title: 'River of grace',
      langue: 'English',
      refrain: 'River of grace, flow through me',
      couplets: [
        { text: 'Let your mercy wash over all I am' },
        { text: 'From your throne my heart receives your plan' }
      ]
    },
    {
      _id: 'c26',
      number: 180,
      title: 'Dieu fidèle',
      langue: 'Français',
      refrain: 'Dieu fidèle, tu ne changes jamais',
      couplets: [
        { text: 'Tu es ma force quand je faiblis' },
        { text: 'Dans tes promesses, je trouve la vie' }
      ]
    },
    {
      _id: 'c27',
      number: 185,
      title: 'Lumière de l’espérance',
      langue: 'Français',
      refrain: 'Ta lumière nous guide vers l’espérance',
      audio: true,
      audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-5.mp3',
      couplets: [
        { text: 'Lumière de l’espérance, brille dans nos vies' },
        { text: 'Fais fuir les ténèbres, ouvre nos esprits' },
        { text: 'Dans chaque matin, entends nos prières' },
        { text: 'Ton souffle nous révèle ta douce lumière' },
        { text: 'Porte nos cœurs vers la vérité' },
        { text: 'Que ton amour guide nos prochains pas' },
        { text: 'Nos âmes te cherchent dans la nuit' },
        { text: 'Tu nous rends confiants en ton infinie bonté' },
        { text: 'Dans la tempête, ton règne demeure' },
        { text: 'Nous marchons ensemble sous ta clarté' }
      ]
    },
    {
      _id: 'c28',
      number: 190,
      title: 'Marche avec moi',
      langue: 'Français',
      refrain: 'Marche avec moi, ne me laisse pas seul',
      audio: true,
      audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-6.mp3',
      couplets: [
        { text: 'Marche avec moi sur le sentier de foi' },
        { text: 'Prends ma main et conduis-moi pas à pas' },
        { text: 'Quand les ombres tombent, sois mon espérance' },
        { text: 'Je veux vivre dans ta présence' },
        { text: 'Chaque matin, je t’offre mon cœur' },
        { text: 'Ta paix habite en moi sans peur' },
        { text: 'Dans tes chemins je trouve ma joie' },
        { text: 'Tu allumes en moi une éternelle flamme' },
        { text: 'Vivant en toi, je proclame ta gloire' },
        { text: 'Marche avec moi pour révéler ta victoire' }
      ]
    },
    {
      _id: 'c29',
      number: 195,
      title: 'Cœur en adoration',
      langue: 'Français',
      refrain: 'Mon cœur se tourne vers toi en adoration',
      audio: true,
      audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-7.mp3',
      couplets: [
        { text: 'Mon cœur en adoration s’élève vers toi' },
        { text: 'Ta présence est ma force et ma joie' },
        { text: 'Je te loue pour ton amour infini' },
        { text: 'Ta bonté rayonne sur ma vie' },
        { text: 'Quand je te cherche, tu me réponds' },
        { text: 'Ton Esprit me conduit vers le bon chemin' },
        { text: 'Je bénis ton nom chaque jour' },
        { text: 'Tu restes mon roc, mon secours toujours' },
        { text: 'Je veux t’adorer de tout mon être' },
        { text: 'Mon cœur se prosterne devant ton mystère' }
      ]
    },
    {
      _id: 'c30',
      number: 200,
      title: 'Hymne de la grâce',
      langue: 'Français',
      refrain: 'Hymne de la grâce, chante ton amour',
      audio: true,
      audioUrl: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-8.mp3',
      couplets: [
        { text: 'Hymne de la grâce, élève nos voix' },
        { text: 'Déclare ta lumière et ta loi' },
        { text: 'Tu nous as lavés, tu nous as rendus libres' },
        { text: 'Nous marchons en paix sous ton aile vive' },
        { text: 'Ton pardon coule comme un fleuve' },
        { text: 'Il restaure tout ce qui est en nous' },
        { text: 'Ta grâce nous soutient chaque jour' },
        { text: 'Ton amour nous comble, ô Seigneur' },
        { text: 'Nous célébrons ton nom en vérité' },
        { text: 'Hymne de la grâce, pour l’éternité' }
      ]
    }
  ];

  // helper to mimic axios response shape
  const ok = (data, ms = 250) => delay(ms).then(() => ({ data }));
  const created = (data, ms = 250) => delay(ms).then(() => ({ data }));
  const removed = (ms = 200) => delay(ms).then(() => ({ data: {} }));
  const error401 = (ms = 100) => delay(ms).then(() => Promise.reject({ response: { status: 401, data: { message: 'Unauthorized' } } }));

  // assign mock implementations
  fetchCantiques = () => ok(mockCantiques);
  getCantique = (id) => ok(mockCantiques.find(c => c._id === id) || null);
  createCantique = (data) => {
    const newItem = { ...data, _id: `c${Date.now()}` };
    mockCantiques.push(newItem);
    return created(newItem);
  };
  updateCantique = (id, data) => {
    const idx = mockCantiques.findIndex(c => c._id === id);
    if (idx === -1) return error401();
    mockCantiques[idx] = { ...mockCantiques[idx], ...data };
    return ok(mockCantiques[idx]);
  };
  deleteCantique = (id) => {
    const target = mockCantiques.find(c => c._id === id);
    mockCantiques = mockCantiques.filter(c => c._id !== id);
    if (target && typeof target.number === 'number' && !mockCantiques.some(c => c.number === target.number)) {
      mockCantiques = mockCantiques.map(c => (c.number > target.number ? { ...c, number: c.number - 1 } : c));
    }
    return ok({ message: 'Deleted', number: target?.number ?? null });
  };
  const planMockRenumber = () => {
    const ordered = [...mockCantiques].sort((a, b) => (a.number ?? Infinity) - (b.number ?? Infinity));
    const changes = ordered
      .map((c, index) => ({ id: c._id, title: c.title, langue: c.langue, from: c.number ?? null, to: index + 1 }))
      .filter(c => c.from !== c.to);
    return { total: ordered.length, changes };
  };
  fetchRenumberPreview = () => ok(planMockRenumber());
  applyRenumber = () => {
    const plan = planMockRenumber();
    const next = new Map(plan.changes.map(c => [c.id, c.to]));
    mockCantiques = mockCantiques.map(c => (next.has(c._id) ? { ...c, number: next.get(c._id) } : c));
    return ok({ total: plan.total, changed: plan.changes.length });
  };
  uploadCantiqueAudio = (file) => {
    const objectUrl = URL.createObjectURL(file);
    return ok({
      hasAudio: true,
      audio: true,
      audioUrl: objectUrl,
      audioFileName: file.name || 'audio.webm',
      audioMimeType: file.type || 'audio/webm',
    });
  };

  fetchLangues = () => ok(mockLangues);
  createLangue = (data) => {
    const newLang = { ...data, _id: `l${Date.now()}` };
    mockLangues.push(newLang);
    return created(newLang);
  };
  updateLangue = (id, data) => {
    const idx = mockLangues.findIndex(l => l._id === id);
    if (idx === -1) return error401();
    mockLangues[idx] = { ...mockLangues[idx], ...data };
    return ok(mockLangues[idx]);
  };
  deleteLangue = (id) => {
    mockLangues = mockLangues.filter(l => l._id !== id);
    return removed();
  };

  login = (credentials) => {
    return ok({ token: 'mock-token' }, 150);
  };
  register = (credentials) => {
    return ok({ created: true }, 150);
  };

  sendVisitorHeartbeat = () => ok(null, 50);
  let mockVisitors = null;
  const seedMockVisitors = () => {
    const now = Date.now();
    return [
      {
        id: 'v1', ip: '41.243.0.12', device: { kind: 'mobile', os: 'Android', browser: 'Chrome' },
        gpsConsent: 'granted', gps: { lat: -4.32142, lng: 15.30807, accuracy: 40, at: new Date(now - 60000).toISOString(), city: 'Kinshasa', country: 'République démocratique du Congo' },
        currentPath: '/cantiques/1', visits: 4, firstSeen: new Date(now - 86400000 * 3).toISOString(), lastSeen: new Date(now - 30000).toISOString(), online: true,
      },
      {
        id: 'v2', ip: '90.12.44.7', device: { kind: 'desktop', os: 'Windows', browser: 'Edge' },
        gpsConsent: 'denied', gps: null,
        currentPath: '/', visits: 1, firstSeen: new Date(now - 7200000).toISOString(), lastSeen: new Date(now - 3600000).toISOString(), online: false,
      },
    ];
  };
  fetchVisitors = () => {
    if (!mockVisitors) mockVisitors = seedMockVisitors();
    const visitors = mockVisitors;
    const countries = new Set(visitors.map((v) => v.gps?.country).filter(Boolean)).size;
    return ok({
      onlineWindowSeconds: 150,
      stats: {
        online: visitors.filter((v) => v.online).length, last24h: visitors.length, total: visitors.length, countries, gpsShared: visitors.filter((v) => v.gps).length,
      },
      visitors,
    });
  };
  deleteVisitor = (id) => {
    mockVisitors = (mockVisitors || []).filter((v) => v.id !== id);
    return ok({ deleted: 1 });
  };
  deleteAllVisitors = () => {
    const deleted = (mockVisitors || []).length;
    mockVisitors = [];
    return ok({ deleted });
  };

  // minimal axios-like mock instance
  const mockInstance = {
    get: (path) => {
      if (path === '/cantiques') return fetchCantiques();
      if (path.startsWith('/cantiques/')) return getCantique(path.split('/')[2]);
      if (path === '/langues') return fetchLangues();
      return ok(null);
    },
    post: (path, data) => {
      if (path === '/cantiques') return createCantique(data);
      if (path === '/cantiques/audio') return uploadCantiqueAudio(data?.get?.('audio') || data);
      if (path === '/langues') return createLangue(data);
      if (path === '/auth/login') return login(data);
      if (path === '/auth/register') return register(data);
      return ok(null);
    },
    put: (path, data) => {
      if (path.startsWith('/cantiques/')) return updateCantique(path.split('/')[2], data);
      if (path.startsWith('/langues/')) return updateLangue(path.split('/')[2], data);
      return ok(null);
    },
    delete: (path) => {
      if (path.startsWith('/cantiques/')) return deleteCantique(path.split('/')[2]);
      if (path.startsWith('/langues/')) return deleteLangue(path.split('/')[2]);
      return removed();
    }
  };

  apiDefault = mockInstance;
}

// Named exports
export { fetchCantiques, getCantique, createCantique, updateCantique, deleteCantique, uploadCantiqueAudio };
export { fetchRenumberPreview, applyRenumber };
export { fetchLangues, createLangue, updateLangue, deleteLangue };
export { login, register };
export { sendVisitorHeartbeat, fetchVisitors, deleteVisitor, deleteAllVisitors };

// Default export (axios instance or mock)
export default apiDefault;
